<template>
  <div class="empresa-inmobiliaria-form" v-if="model != null">
    <DxTabPanel height="95%" tabs-position="top" icon-position="left" :defer-rendering="false">
      <DxItem :key="1" :title="tituloTabDatos" class="tab-item" icon="fa-regular fa-file-lines">
        <DxForm id="formularioEmpresaAgentes" ref="formInstance" :form-data="model" label-location="top" :show-colon-after-label="false"
          validation-group="validacionEmpresaInmobiliaria">
          <!-- xs: 768, sm: 992, md:1200 -->
          <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

          <!-- Datos de la empresa -->
          <DxGroupItem :col-span="6" :col-count="6">
            <DxSimpleItem data-field="RazonSocial" editor-type="dxTextBox" :col-span="3">
              <DxLabel :text="$t('empresas_inmobiliarias.form.razon_social')" />
              <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.razon_social')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Documento" editor-type="dxTextBox" :col-span="3">
              <DxLabel :text="$t('empresas_inmobiliarias.form.cif')" />
              <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.cif')" />
              <DxPatternRule :pattern="cifPattern" :message="$t('empresas_inmobiliarias.form.validacion.cif_patron')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Web" editor-type="dxTextBox" :col-span="6">
              <DxLabel :text="$t('empresas_inmobiliarias.form.sitio_web')" />
              <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.sitio_web')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Telefono" editor-type="dxTextBox" :col-span="3">
              <DxLabel :text="$t('empresas_inmobiliarias.form.telefono')" />
              <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.telefono')" />
              <DxPatternRule :pattern="telefonoPattern" :message="$t('empresas_inmobiliarias.form.validacion.telefono_patron')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Email" editor-type="dxTextBox" :col-span="3">
              <DxLabel :text="$t('empresas_inmobiliarias.form.email')" />
              <DxRequiredRule :message="$t('empresas_inmobiliarias.form.validacion.email')" />
              <DxEmailRule :message="$t('empresas_inmobiliarias.form.validacion.email_patron')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="FechaCreacion" editor-type="dxDateBox" :col-span="2">
              <DxLabel :text="$t('empresas_inmobiliarias.form.fecha_creacion')" />
              <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.fecha_creacion')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="FormaJuridica" editor-type="dxSelectBox" :col-span="2"
              :editor-options="getLookup('empresas_inmobiliarias', 'forma_juridica')">
              <DxLabel :text="$t('empresas_inmobiliarias.form.forma_juridica')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="DenoFactura" editor-type="dxTextBox" :col-span="2">
              <DxLabel :text="$t('empresas_inmobiliarias.form.deno_factura')" />
              <DxPatternRule :pattern="denoFacturaPattern" :message="$t('mis_datos.datos_fiscales.validacion.deno_factura_patron')" />
              <DxRequiredRule :message="$t('mis_datos.datos_fiscales.validacion.deno_factura')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Iban" editor-type="dxTextBox" :col-span="3" :editor-options="ibanOptions">
              <DxLabel :text="$t('empresas_inmobiliarias.form.iban')" />
            </DxSimpleItem>
            <DxEmptyItem :col-span="1"></DxEmptyItem>
            <DxSimpleItem data-field="MarcoComisiones" editor-type="dxSelectBox" :col-span="2"
              :editor-options="{ ...getSelectBoxDefaultProperties(), ...selectMarcoComisionesOptions }">
              <DxLabel :text="$t('empresas_inmobiliarias.form.marco_comisiones')" />
            </DxSimpleItem>
          </DxGroupItem>

          <!-- Usuario responsable -->
          <DxGroupItem :col-span="6" :col-count="6">
            <DxSimpleItem data-field="UsuarioResponsable" editor-type="dxSelectBox" :col-span="3"
              :editor-options="{ ...getSelectBoxDefaultProperties(), ...selectResponsableOptions }">
              <DxLabel :text="$t('empresas_inmobiliarias.form.usuario_responsable')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="IdComercial" editor-type="dxSelectBox" :col-span="3"
              :editor-options="{ ...getSelectBoxDefaultProperties(), ...selectComercialOptions }">
              <DxLabel :text="$t('empresas_colaboradoras.form.id_comercial')" />
            </DxSimpleItem>
          </DxGroupItem>

          <!-- Otras configuraciones -->
          <DxGroupItem :col-span="6" :col-count="6">

            <!-- Aplica IRPF -->
            <DxGroupItem :col-span="2" :visible="!soySociedad">
              <DxSimpleItem>
                <DxRadioGroup v-model="aplicarIrpf" :items="radioYesNoValues" value-expr="valor" display-expr="texto" layout="horizontal"
                  validation-message-mode="always">
                </DxRadioGroup>
                <DxLabel :text="$t('empresas_colaboradoras.form.aplica_irpf')" />
              </DxSimpleItem>
              <DxSimpleItem data-field="PorcentajeIrpf" editor-type="dxNumberBox" :editor-options="irpfNumberOptions" :visible="aplicarIrpf">
                <DxLabel :text="$t('empresas_colaboradoras.form.irpf')" />
                <DxCompareRule :comparison-target="() => 0" :comparison-type="'>='" :message="$t('empresas_colaboradoras.form.validacion.irpf_mayor_0')" />
              </DxSimpleItem>
            </DxGroupItem>

            <!-- Con autofactura -->
            <DxGroupItem :col-span="2">
              <DxSimpleItem>
                <DxRadioGroup v-model="conAutofactura" :items="radioYesNoValues" value-expr="valor" display-expr="texto" layout="horizontal"
                  validation-message-mode="always">
                </DxRadioGroup>
                <DxLabel :text="$t('empresas_colaboradoras.form.con_autofactura')" />
              </DxSimpleItem>
              <DxSimpleItem data-field="DiasAutofacturacion" editor-type="dxTagBox" :col-span="2" :editor-options="diasAutofacturacionOptions"
                :visible="conAutofactura">
                <DxLabel :text="$t('empresas_colaboradoras.form.dias_autofacturacion')" />
              </DxSimpleItem>
            </DxGroupItem>

            <!-- Whitelist servicios -->
            <DxGroupItem :col-span="2">
              <DxSimpleItem v-if="model">
                <DxRadioGroup v-model="model!.TieneWhitelistServicios" :items="radioYesNoValues" value-expr="valor" display-expr="texto" layout="horizontal"
                  validation-message-mode="always">
                </DxRadioGroup>
                <DxLabel template="whitelistLabel" />
              </DxSimpleItem>
              <DxSimpleItem data-field="WhitelistServicios" editor-type="dxTagBox" :col-span="2"
                :editor-options="{ ...getSelectorEmpresaServiciosProperties(), ...selectEmpresaServiciosOptions }" :visible="model?.TieneWhitelistServicios">
                <DxLabel text=" " />
              </DxSimpleItem>
            </DxGroupItem>

            <DxEmptyItem :col-span="2" :visible="soySociedad"></DxEmptyItem>

          </DxGroupItem>

          <!-- Gestión de agua -->
          <DxGroupItem :col-span="6" :col-count="6">
            <DxSimpleItem v-if="model" :col-span="2">
              <DxRadioGroup v-model="model.PuedeTramitarAgua" :items="radioYesNoValues" value-expr="valor" display-expr="texto" layout="horizontal"
                validation-message-mode="always">
              </DxRadioGroup>
              <DxLabel template="gestionAguaLabel" />
            </DxSimpleItem>
            <DxEmptyItem :col-span="4"></DxEmptyItem>
          </DxGroupItem>

          <!-- Dirección física -->
          <DxGroupItem :col-span="6" :col-count="6">
            <DxSimpleItem :col-span="6">
              <span class="dx-field-item-label-text text-success">{{
                $t("empresas_inmobiliarias.form.direccion_fisica")
                }}</span>
            </DxSimpleItem>
            <DxSimpleItem data-field="Direccion.CodigoPostal" editor-type="dxTextBox" :col-span="1">
              <DxLabel :text="$t('empresas_inmobiliarias.form.codigo_postal')" />
              <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.codigo_postal')" />
              <DxPatternRule :pattern="codigoPostalPattern" :message="$t('empresa_inmobiliaria.form.validacion.codigo_postal_patron')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Direccion.Provincia" editor-type="dxTextBox" :col-span="2">
              <DxLabel :text="$t('empresas_inmobiliarias.form.provincia')" />
              <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.provincia')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Direccion.Municipio" editor-type="dxTextBox" :col-span="3">
              <DxLabel :text="$t('empresas_inmobiliarias.form.municipio')" />
              <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.municipio')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Direccion.TipoVia" editor-type="dxTextBox" :col-span="2">
              <DxLabel :text="$t('empresas_inmobiliarias.form.tipo_de_via')" />
              <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.tipo_de_via')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Direccion.NombreVia" editor-type="dxTextBox" :col-span="4">
              <DxLabel :text="$t('empresas_inmobiliarias.form.nombre_via')" />
              <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.nombre_via')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Direccion.Numero" editor-type="dxTextBox" :col-span="1">
              <DxLabel :text="$t('empresas_inmobiliarias.form.numero')" />
              <DxRequiredRule :message="$t('empresa_inmobiliaria.form.validacion.numero')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Direccion.Escalera" editor-type="dxTextBox" :col-span="1">
              <DxLabel :text="$t('empresas_inmobiliarias.form.escalera')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Direccion.Piso" editor-type="dxTextBox" :col-span="1">
              <DxLabel :text="$t('empresas_inmobiliarias.form.piso')" />
            </DxSimpleItem>
            <DxSimpleItem data-field="Direccion.Puerta" editor-type="dxTextBox" :col-span="1">
              <DxLabel :text="$t('empresas_inmobiliarias.form.puerta')" />
            </DxSimpleItem>
            <DxEmptyItem :col-span="2">
            </DxEmptyItem>
          </DxGroupItem>

          <!-- Imagen de la empresa -->
          <DxGroupItem :col-span="6" :col-count="6">
            <DxSimpleItem :col-span="3">
              <DxFileUploader :accept="'image/*'" :multiple="false" @valueChanged="onFileUploaderValueChanged">
              </DxFileUploader>
              <DxLabel :text="$t('empresas_inmobiliarias.form.imagen')" />
            </DxSimpleItem>
            <DxSimpleItem :visible="props.model?.ImagenB64 != '' && props.model?.ImagenB64 != null" :col-span="3">
              <div>
                <img :src="props.model?.ImagenB64!" alt="Imagen de la empresa de agentes" style="max-width: 100%; margin-top: 10px;" />
              </div>
            </DxSimpleItem>
          </DxGroupItem>

          <!-- Logo del portal -->
          <DxGroupItem :col-span="6" :col-count="6">
            <DxSimpleItem :col-span="3">
              <DxFileUploader :accept="'image/*'" :multiple="false" @valueChanged="onLogoPortalFileUploaderValueChanged">
              </DxFileUploader>
              <DxLabel :text="$t('empresas_inmobiliarias.form.logoPortal')" />
            </DxSimpleItem>
            <DxSimpleItem :visible="props.model?.LogoPortalB64 != '' && props.model?.LogoPortalB64 != null" :col-span="3">
              <div>
                <img :src="props.model?.LogoPortalB64!" alt="Logo del portal de la empresa" style="max-width: 60px; max-height: 60px; margin-top: 10px;" />
              </div>
            </DxSimpleItem>
          </DxGroupItem>


          <template #gestionAguaLabel="{ data }">
            <div id="template-content">
              <span class="dx-field-item-label-text">{{ $t('empresas_inmobiliarias.form.puede_gestionar_agua') }}</span>
              <div class="info-icon d-inline text-primary ms-1">
                <i class="fas fa-info-circle" id="gestionAguaLabel"></i>
                <DxTooltip :hide-on-outside-click="false" target="#gestionAguaLabel" show-event="mouseenter" hide-event="mouseleave">
                  <template #content>
                    <div id="tooltip-content">{{ $t('empresas_inmobiliarias.form.texto_informativo_gestion_agua') }}</div>
                  </template>
                </DxTooltip>
              </div>
            </div>
          </template>

          <template #whitelistLabel="{ data }">
            <div id="template-content">
              <span class="dx-field-item-label-text">{{ $t('empresas_colaboradoras.form.con_whitelist_servicios') }}</span>
              <div class="info-icon d-inline text-primary ms-1">
                <i class="fas fa-info-circle" id="whitelistLabel"></i>
                <DxTooltip :hide-on-outside-click="false" target="#whitelistLabel" show-event="mouseenter" hide-event="mouseleave">
                  <template #content>
                    <div id="tooltip-content">{{ $t('empresas_colaboradoras.form.texto_informativo_whitelist_servicios') }}</div>
                  </template>
                </DxTooltip>
              </div>
            </div>
          </template>
        </DxForm>
      </DxItem>
      <DxItem :key="2" :title="tituloTabDocumentacion" class="tab-item" icon="fa-regular fa-folder-open">
        <div v-if="mostrarDocumentacion" class="tab-documentacion">
          <empresa-agentes-documentacion-panel
            ref="tabDocumentacionRef"
            :id-empresa-agentes="idDocumentacionEmpresaAgentes"
            :estructura-documental="estructuraDocumentalEmpresaAgentes"
            sync-documentacion-externa
            documentos-opcionales
          />
        </div>
        <div v-else class="p-4 text-muted">
          {{ textoPlaceholderDocumentacion }}
        </div>
      </DxItem>
    </DxTabPanel>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from '@components/dialogs/message-box';
import { FormaJuridicaEnum, type CreateEmpresaInmobiliariaModel, type EditEmpresaInmobiliariaModel, type EstructuraDocumentalEmpresaAgentesModel } from '@api/api';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { DxFileUploader, DxTagBox, type DxButton } from 'devextreme-vue';
import DxTabPanel, { DxItem } from 'devextreme-vue/tab-panel';
import { getIbanOptions, } from "@helpers/text-box-helper";
import {
  DxEmailRule,
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxPatternRule,
  DxCompareRule,
  DxEmptyItem,
  DxGroupItem,
  DxSimpleItem
} from "devextreme-vue/form";
import { DxRadioGroup } from 'devextreme-vue';
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from 'vue';
import { useIntl } from '@services/i18n';
import { getLookup, getColumnsLookupAsNumber, getSelectBoxDefaultProperties } from '@helpers/grid-helper';
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from '@api/integration';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { ApiError } from '@/.shared/services/http';
import { COMPANY_WITH_DUPLICATED_DENO_FACTURA, COMPANY_WITH_DUPLICATED_CIF } from '@/.shared/errors/codes';
import { DxTooltip } from 'devextreme-vue/tooltip';
import EmpresaAgentesDocumentacionPanel from '@components/empresa-agentes/EmpresaAgentesDocumentacionPanel.vue';
import {
  createAsyncSerialTaskRunner,
  mergeEstructuraDocumentalEmpresaAgentesPreservandoAdjuntosComunes,
} from "@/components/empresas-agentes/empresa-agentes-estructura-documental-helper";

const api = useApi();
const ejecutarActualizacionEstructuraDocumental = createAsyncSerialTaskRunner();
const formInstance = ref<DxForm>();
const intl = useIntl();
const props = defineProps({
  model: {
    type: Object as () => EditEmpresaInmobiliariaModel | CreateEmpresaInmobiliariaModel,
    required: false
  },
  isEditMode: {
    type: Boolean,
    required: true
  }
});
const conAutofactura = ref<boolean>(false);
const aplicarIrpf = ref<boolean>(false);
const selectEmpresaServiciosOptions = reactive<DxTagBox>({ dataSource: [] });
const selectMarcoComisionesOptions = reactive<any>({
  showClearButton: true,
  dataSource: [],
});
const selectResponsableOptions = reactive<any>({
  showClearButton: true,
  dataSource: [],
});
const selectComercialOptions = reactive<any>({
  showClearButton: true,
  dataSource: [],
});
const isFormLoading = ref<boolean>(true);

const idDocumentacionEmpresaAgentes = ref<string | null>(null);
const estructuraDocumentalEmpresaAgentes = ref<EstructuraDocumentalEmpresaAgentesModel[]>([]);
const tabDocumentacionRef = ref<InstanceType<typeof EmpresaAgentesDocumentacionPanel>>();

const tituloTabDatos = computed(() => intl.i18n.t("empresa_agente.form.datos"));
const tituloTabDocumentacion = computed(() => intl.i18n.t("empresa_agente.form.documentos"));

const mostrarDocumentacion = computed(() =>
  props.isEditMode ? !!idDocumentacionEmpresaAgentes.value : estructuraDocumentalEmpresaAgentes.value.length > 0,
);

const textoPlaceholderDocumentacion = computed(() => {
  if (!props.isEditMode && estructuraDocumentalEmpresaAgentes.value.length === 0) {
    return intl.i18n.t("empresas_inmobiliarias.form.documentacion_indique_forma_juridica");
  }
  return intl.i18n.t("empresas_inmobiliarias.form.documentacion_tras_guardar");
});

const cifPattern = ref<RegExp>(/^[A-Za-z]\d{7}[A-Za-z0-9]$|^\d{8}[A-Za-z]$/);
const telefonoPattern = ref<RegExp>(/^\d{9}$/);
const codigoPostalPattern = ref<RegExp>(/^\d{5}$/);
const denoFacturaPattern = ref<RegExp>(/^[A-Za-zÑñáéíóúüÁÉÍÓÚÜ0-9]{1,7}$/);

const cargarDocumentacion = async (): Promise<void> => {
  await ejecutarActualizacionEstructuraDocumental(async () => {
    if (!props.model) {
      return;
    }
    if (!props.isEditMode || !("Id" in props.model)) {
      idDocumentacionEmpresaAgentes.value = null;
      return;
    }
    const id = (props.model as EditEmpresaInmobiliariaModel).Id;
    idDocumentacionEmpresaAgentes.value = id;
    estructuraDocumentalEmpresaAgentes.value =
      (await api.callStandardApi("getEstructuraDocumentalEmpresaAgentes", { showLoading: false }, id)) ?? [];
  });
};

const aplicarEstructuraDocumentalPorFormaJuridica = async (): Promise<void> => {
  const fj = props.model?.FormaJuridica;
  if (fj == null || fj === undefined) {
    return;
  }
  await ejecutarActualizacionEstructuraDocumental(async () => {
    const anterior = [...estructuraDocumentalEmpresaAgentes.value];
    const nueva =
      (await api.callStandardApi("getEstructuraDocumentalEmpresaAgentesByFormaJuridica", { showLoading: false }, fj)) ??
      [];
    estructuraDocumentalEmpresaAgentes.value = mergeEstructuraDocumentalEmpresaAgentesPreservandoAdjuntosComunes(
      nueva,
      anterior,
    );
  });
};

onMounted(async() => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!);

  configureButtons(modal);

  await cargarDatos();
});

const cargarDatos = async () => {
  try {
    conAutofactura.value = props.model?.DiasAutofacturacion ? props.model.DiasAutofacturacion.length > 0 : false;
    aplicarIrpf.value = props.model?.PorcentajeIrpf ? (props.model.PorcentajeIrpf != null && props.model.PorcentajeIrpf != 0) : false;

    if (props.isEditMode) {
      void cargarDocumentacion().catch(() => {
        idDocumentacionEmpresaAgentes.value = null;
        estructuraDocumentalEmpresaAgentes.value = [];
      });
    }

    const [marcos, responsables, servicios, comerciales] = await Promise.all([
      api.callStandardApi("getMarcosComisionesList", { showLoading: false }),
      getResponsablesSelectorDatasource(),
      api.callStandardApi("getEmpresasServiciosSelector", { showLoading: false }),
      api.callStandardApi("getUsuariosComercialesSelector", { showLoading: false }),
    ]);

    selectMarcoComisionesOptions.dataSource = marcos;
    selectResponsableOptions.dataSource = responsables;
    selectEmpresaServiciosOptions.dataSource = servicios;
    selectComercialOptions.dataSource = comerciales;
  } finally {
    isFormLoading.value = false;
  }
};

const getResponsablesSelectorDatasource = async () => {
  if (props.isEditMode) {
    return await api.callStandardApi("getResponsablesSelectorByIdEmpresaAgentes", { showLoading: false }, (props.model as EditEmpresaInmobiliariaModel).Id);
  } else {
    return await api.callStandardApi("getUsuariosBackofficeSelector", { showLoading: false });
  }
}

function getSelectorEmpresaServiciosProperties(): DxTagBox {
  return {
    valueExpr: 'Id',
    displayExpr: 'Denominacion',
    searchEnabled: true
  }
}

watch(() => conAutofactura.value, (newVal) => {
  if (!newVal)
    props.model!.DiasAutofacturacion = [];
})

watch(() => aplicarIrpf.value, (newVal) => {
  if (!newVal)
    props.model!.PorcentajeIrpf = 0;
});

watch(
  () => props.model?.FormaJuridica,
  async (nueva, previa) => {
    if (nueva == null || nueva === undefined) {
      return;
    }
    if (nueva === previa) {
      return;
    }
    await aplicarEstructuraDocumentalPorFormaJuridica();
  },
);

const radioYesNoValues = [
  { valor: true, texto: intl.i18n.t("shared.ui.literals.yes") },
  { valor: false, texto: intl.i18n.t("shared.ui.literals.no") },
];

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const irpfNumberOptions = {
  showSpinButtons: true,
  step: 0.01,
  format: {
    type: "fixedPoint",
    precision: 2,
    formatter: (value: number) => {
      return intl.i18n.n(Number(value.toFixed(2)), 'decimal');
    }
  },
  min: 0,
  max: 100
};

const diasAutofacturacionOptions = {
  dataSource: getColumnsLookupAsNumber('empresas_inmobiliarias', 'dias_autofacturacion').dataSource,
  valueExpr: 'value',
  displayExpr: 'text'
}

const handleIbanValueChanged = (e: any) => {
  const upperValue = e.value?.toUpperCase().replace(/ /g, "");
  props.model!.Iban = upperValue
    ? `ES${upperValue.replace(/^ES/, '')}`
    : null;
};

const ibanOptions = {
  ...getIbanOptions(),
  onValueChanged: handleIbanValueChanged
};

const validarDocumentacionSiAplica = (): boolean => {
  if (estructuraDocumentalEmpresaAgentes.value.length === 0) {
    return true;
  }
  const docTab = tabDocumentacionRef.value;
  if (!docTab) {
    return true;
  }
  if (!docTab.isDocumentacionValid()) {
    toastError(intl.i18n.t("empresa_agentes.documentacion.documento_validaciones_fallo"));
    return false;
  }
  return true;
};

const saveOrEdit = async () => {
  var res = validationEngine.validateGroup("validacionEmpresaInmobiliaria");
  if (res.isValid) {
    if (!validarDocumentacionSiAplica()) {
      return false;
    }
    if (props.isEditMode) {
      try {
        await api.callStandardApi("editEmpresaInmobiliaria", { showLoading: true }, props.model as EditEmpresaInmobiliariaModel);
        try {
          await tabDocumentacionRef.value?.sincronizarCambiosDocumentacion(undefined, { suprimirToastExito: true });
        } catch {
          toastError(intl.i18n.t("empresa_agentes.documentacion.guardar_fallo"));
          return false;
        }
        await cargarDocumentacion();
        toastSuccess(intl.i18n.t("empresa_inmobiliaria.edicion_exito"));
      } catch (e: any) {
        if (e instanceof ApiError && e.details?.errorList.length === 1 && [COMPANY_WITH_DUPLICATED_DENO_FACTURA, COMPANY_WITH_DUPLICATED_CIF].includes(e.details.errorList[0].Code)) {
          toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
        }
        else throw e;
        return false;
      }
    } else {
      try {
        const nuevoId = await api.callStandardApi(
          "createEmpresaInmobiliaria",
          { showLoading: true },
          props.model as CreateEmpresaInmobiliariaModel,
        );
        if (typeof nuevoId !== "string" || nuevoId.trim() === "") {
          toastError(intl.i18n.t("empresa_agentes.documentacion.guardar_fallo"));
          return false;
        }
        try {
          await tabDocumentacionRef.value?.sincronizarCambiosDocumentacion(nuevoId, { suprimirToastExito: true });
        } catch {
          toastError(intl.i18n.t("empresa_agentes.documentacion.guardar_fallo"));
          return false;
        }
        toastSuccess(intl.i18n.t('empresa_inmobiliaria.creacion_exito'));
      } catch (e: any) {
        if (e instanceof ApiError && e.details?.errorList.length === 1 && [COMPANY_WITH_DUPLICATED_DENO_FACTURA, COMPANY_WITH_DUPLICATED_CIF].includes(e.details.errorList[0].Code)) {
          toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
        }
        else throw e;
        return false;
      }
    }
    return true;
  } else {
    toastError(intl.i18n.t('empresa_inmobiliaria.revise_validacion'));
  }

  return false;
}

function onFileUploaderValueChanged(e: any) {
  const file = e.value[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = function (event) {
      const base64 = (event.target as FileReader).result as string;
      if (props.model != null)
        props.model.ImagenB64 = base64;
    };
    reader.readAsDataURL(file);
  }
};

function onLogoPortalFileUploaderValueChanged(e: any) {
  const file = e.value[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = function (event) {
      const base64 = (event.target as FileReader).result as string;
      if (props.model != null)
        props.model.LogoPortalB64 = base64;
    };
    reader.readAsDataURL(file);
  }
};

const soySociedad = computed(() => {
  return props.model?.FormaJuridica == FormaJuridicaEnum.SociedadLimitada;
});

</script>

<style scoped lang="scss">
.tab-documentacion {
  min-height: 320px;
}
</style>